import { boot } from 'quasar/wrappers'
import { validInt } from 'booksprout'

import { authenticationService } from 'booksprout-app/src/modules/authentication/authenticationService'
import authStore from 'booksprout-app/src/modules/authentication/store'

/**
 * Load the signed in user on the marketing site.
 *
 * The website shares the session with the author app, but nothing here ever asked the API who the
 * visitor was, so a signed in reader browsing these pages was anonymous to us - most visibly to the
 * Help Scout Beacon, which shares the author app's Beacon and so filed their conversations as
 * "Unidentified Customer" [sc-27755].
 *
 * `load()` commits the user without going through `setUser`, so the second call below is what
 * populates `window.GlobalUserData` and identifies the visitor to Beacon. The user is taken from
 * state rather than the `authenticatedUser` getter because that getter decorates the user with a
 * `permissionQuery`, which has no business being written back into the store.
 *
 * Client only (`server: false` in quasar.conf): Beacon only exists in the browser and `setUser`
 * writes to `window`. SSR renders therefore never make this call, nor do crawlers that do not run JS.
 *
 * `load()` asks the API who the visitor is even when no session is readable, because a missing
 * expiry mirror can still hide a live one (sc-27671). That is deliberately left alone rather than
 * gated on the mirror being present: boot files run once per visit - client side navigation does not
 * re-run them - so the call such a gate would save is one per visitor, alongside the
 * GTM/Hotjar/Termly/Beacon calls the page already makes. Not worth the extra moving part.
 */
export default boot(async () => {
  try {
    const auth = authenticationService()
    await auth.load()

    const user = authStore.state.user
    if (validInt(user?.id)) {
      auth.setUser(user as never)
    }
  } catch {
    // As in the other apps' route guards: the marketing pages must still render if the auth call
    // fails, so a signed out visitor and an unreachable API look the same here.
  }
})
